// Editable · Input
//
// Wraps AntD <Input> with DS tokens for consistent focus/border/disabled
// states and typography across the editable tier. Exposes a clean
// Input/Output contract; hides the deprecated `bordered` prop foot-gun
// (removed in AntD v5 — use `variant` instead).

import React, { useEffect, useRef } from "react";
import { Input as AntInput } from "antd";
import type { InputProps as AntInputProps } from "antd";

// ─── Inputs (data props — the model) ─────────────────────────────────────────

export interface InputInput {
  value?: string;
  /** Placeholder text. */
  placeholder?: string;
  /** Disable the input. Default false. */
  disabled?: boolean;
  /**
   * AntD size. Default "middle".
   * Aligns with Select's `size` pattern ("sm" | "md" | "lg") but mapped
   * directly to AntD so inline-edit consumers don't need a translation layer.
   */
  size?: "small" | "middle" | "large";
  /** Validation status — passes through to AntD's status prop. */
  status?: "" | "warning" | "error";
  /** Prefix element rendered inside the left edge of the input. */
  prefix?: React.ReactNode;
  /** Suffix element rendered inside the right edge of the input. */
  suffix?: React.ReactNode;
  /** Allow clearing the value via a clear button. Default false. */
  allowClear?: boolean;
  /**
   * Max character count. Renders a character counter when provided.
   * Passed as AntD `maxLength`.
   */
  maxLength?: number;
  /** Auto-focus on mount — for inline-edit affordances. */
  autoFocus?: boolean;
  /** `id` forwarded to the underlying input for AntD Form.Item label association. */
  id?: string;
  /** `data-testid` on the root element. */
  testId?: string;
  /**
   * Input type. Default "text".
   * Only "text" | "password" | "search" are exposed; for numeric entry use
   * `@ds/forms/WeightInput` or a dedicated typed field.
   */
  type?: "text" | "password" | "search";
}

// ─── Outputs (events emitted — controller hooks) ──────────────────────────────

export interface InputOutput {
  onChange?: (value: string) => void;
  /** Fires on Enter key — commit for inline-edit. */
  onPressEnter?: () => void;
  /** Fires on blur — cancel-on-blur for inline-edit. */
  onBlur?: () => void;
  /** Raw keyboard handler for Escape / custom binding. */
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}

export type InputProps = InputInput & InputOutput;

// ─── Size helper ──────────────────────────────────────────────────────────────

const ANT_SIZE: Record<NonNullable<InputInput["size"]>, AntInputProps["size"]> = {
  small:  "small",
  middle: "middle",
  large:  "large",
};

// ─── Component ────────────────────────────────────────────────────────────────

export function Input({
  value,
  placeholder,
  disabled = false,
  size = "middle",
  status,
  prefix,
  suffix,
  allowClear = false,
  maxLength,
  autoFocus,
  id,
  testId,
  type = "text",
  onChange,
  onPressEnter,
  onBlur,
  onKeyDown,
}: InputProps) {
  // AntD lands rest-props (incl. `data-testid`) on the affix wrapper when
  // `allowClear`/`prefix`/`suffix` is set — not on the fillable <input>. Stamp
  // the testid onto the inner <input> directly so e2e specs can
  // `getByTestId(...).fill(...)` regardless of affix, without a banned
  // `.locator("input")`.
  const rootRef = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    if (!testId) return;
    const input = rootRef.current?.querySelector("input");
    if (input) input.setAttribute("data-testid", testId);
  }, [testId, type, allowClear, prefix, suffix]);

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    onChange?.(e.target.value);
  }

  const sharedProps: AntInputProps = {
    value,
    placeholder,
    disabled,
    size: ANT_SIZE[size],
    status,
    prefix,
    suffix,
    allowClear,
    maxLength,
    autoFocus,
    id,
    onBlur,
    onKeyDown,
    onChange: handleChange,
    style: { width: "100%" },
  };

  if (type === "password") {
    return (
      <span ref={rootRef} style={{ display: "block", width: "100%" }}>
        <AntInput.Password
          {...sharedProps}
          onPressEnter={onPressEnter}
        />
      </span>
    );
  }

  return (
    <span ref={rootRef} style={{ display: "block", width: "100%" }}>
      <AntInput
        {...sharedProps}
        type={type}
        onPressEnter={onPressEnter}
      />
    </span>
  );
}
